// Låntakers lånestatus — backed by GET /chat/loan-status (src/worker/index.ts).
// "Mine selskaper": samme innlogging kan se flere selskaper (hoved-kontakt på ett,
// agent/kontaktperson på andre, matchet på e-post server-side).
import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/lib/apiClient";

export interface SecurityItem {
  amount: number | null;
  owner: string | null;
  ownerName: string | null;
}

// Per-payment breakdown straight from oblinor.no — samme form som kontor-vue sin
// PaymentHistory.vue viser (dato, rentesats, forfalt/betalt rente, hovedstol, saldo).
export interface PaymentDetail {
  paymentNumber: number;
  principalBefore: number;
  unpaidInterestBefore: number;
  periodStart: string;
  periodEnd: string;
  days: number;
  paymentDate: string;
  paymentAmount: number;
  isAfterExpiry: boolean;
  ratePercent: number;
  interestDue: number;
  interestPaid: number;
  principalPaid: number;
  principalRemaining: number;
  unpaidInterestRemaining: number;
}

export interface LoanStatusLine {
  loanId: number;
  loanerId: number;
  companyName: string;
  address: string;
  matrikkel: string | null;
  kommune: string | null;
  security: SecurityItem[];
  principal: number;
  accruedInterest: number;
  outstanding: number;
  rate: number;
  extended: boolean;
  extensionRate: number | null;
  datePayout: string | null;
  dateExpire: string | null;
  extensionDue: string | null;
  datePayedBack: string | null;
  lastPaymentDate: string | null;
  repaidAmount: number | null;
  isOpen: boolean;
  payments: PaymentDetail[];
}

export interface AddressGroup {
  address: string;
  loans: LoanStatusLine[];
  principal: number;
  accruedInterest: number;
  outstanding: number;
}

export interface CompanyGroup {
  loanerId: number;
  companyName: string;
  groups: AddressGroup[];
  principal: number;
  accruedInterest: number;
  outstanding: number;
}

export interface LoanStatusResult {
  asOf: string;
  loanCount: number;
  companyCount: number;
  totalPrincipal: number;
  totalAccruedInterest: number;
  totalOutstanding: number;
  companies: CompanyGroup[];
  allLoans: LoanStatusLine[];
}

export function useLoanStatus() {
  const query = useQuery({
    queryKey: ["loan-status"],
    queryFn: () => apiClient.get<{ ok: true; status: LoanStatusResult }>("/chat/loan-status"),
  });
  return {
    status: query.data?.status,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
